import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async (n = 0) => {
      if (cancelled) return;
      if (n >= 8) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/subscription/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        setAttempts(n + 1);
        timerRef.current = setTimeout(() => poll(n + 1), 2000);
      } catch {
        setAttempts(n + 1);
        timerRef.current = setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sessionId]);

  return (
    <div className="min-h-[50vh] flex items-center justify-center" data-testid="subscription-success-page">
      <div className="card-soft p-10 max-w-md text-center">
        {status === "polling" && (
          <>
            <Loader2 className="mx-auto animate-spin text-[#4A6148]" size={36} />
            <div className="font-heading text-xl font-medium mt-4">Confirming your payment…</div>
            <div className="text-sm text-[#59605C] mt-1">Attempt {attempts + 1} of 8</div>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-[#3B5B3E]" size={44} />
            <div className="font-heading text-2xl font-medium mt-4 text-[#2C302E]">You’re subscribed 🎉</div>
            <div className="text-sm text-[#59605C] mt-2">Thanks for supporting What&rsquo;s Your Balance?</div>
            <button className="btn-primary mt-6" onClick={() => navigate("/app/dashboard")} data-testid="subscription-success-back">
              Back to dashboard
            </button>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <div className="font-heading text-xl font-medium text-[#8C3A30]">Payment not confirmed</div>
            <div className="text-sm text-[#59605C] mt-2">
              {status === "expired" ? "The checkout session expired." :
                status === "timeout" ? "We couldn’t confirm the payment yet. Please try again in a moment." :
                  "Something went wrong."}
            </div>
            <button className="btn-secondary mt-6" onClick={() => navigate("/app/subscription")}>Back to subscription</button>
          </>
        )}
      </div>
    </div>
  );
}
